import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import React from "react";

import { MarkdownContent } from "~/components/markdown-content";
import { ButtonLink } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { CDNImage } from "~/components/ui/cdn-image";
import { ResourceCard } from "~/components/ui/resource-card";
import { Skeleton } from "~/components/ui/skeleton";
import { coachQueryOptions } from "~/lib/query-options";
import { createArticleStructuredData, createMetaTags } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/$username/_profile/resources/$resourceId/",
)({
  loader: async ({ context, params }) => {
    const { username, resourceId } = params;

    // Use consistent query options for coach data
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(username),
    );

    if (!coach) {
      throw redirect({ to: "/" });
    }

    // Find the resource in the already-loaded coach data
    const resource = coach.resources?.find((r) => r.id === resourceId);
    if (!resource) {
      throw notFound();
    }

    // Get other resources for "related" section
    const relatedResources =
      coach.resources?.filter((r) => r.id !== resourceId).slice(0, 4) || [];

    return { coach, resource, relatedResources };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return createMetaTags({
        title: "Resource | Dream Coach Match",
        description: "Coaching resource",
        noindexFollow: true,
      });
    }

    const { coach, resource } = loaderData;
    const description =
      resource.description ||
      `${resource.title} — a free coaching resource by ${coach.fullName} on Dream Coach Match.`;
    const imageUrl = resource.imageSrc || coach.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/resources/${params.resourceId}`;

    // The article's own headline leads the title — not the coach's profile
    // title, which made all resource pages near-duplicates in search.
    const metaTags = createMetaTags({
      title: `${resource.title} | Dream Coach Match`,
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "article",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coach.fullName,
      publishedTime: resource.publishedAt?.toISOString(),
      modifiedTime: resource.publishedAt?.toISOString(),
      section: "Resources",
      keywords: [
        "coaching",
        "resource",
        coach.profile?.coachingTitle || "life coach",
        "guide",
        "tips",
      ],
    });

    // Add structured data for the article
    const structuredData = createArticleStructuredData({
      headline: resource.title,
      description,
      image: imageUrl || undefined,
      author: coach.fullName,
      publishedDate: resource.publishedAt?.toISOString(),
      modifiedDate: resource.publishedAt?.toISOString(),
      url: currentUrl,
    });

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: ResourcePage,
});

function ResourceSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading resource"
      className="mx-auto w-full max-w-5xl px-4 pb-12"
    >
      <Card>
        <CardContent>
          <div className="mb-8 flex flex-col gap-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="aspect-40/21 w-full rounded-lg" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-11/12" />
            <Skeleton className="h-5 w-4/5" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResourcePage() {
  const { username } = Route.useParams();
  const { coach, resource, relatedResources } = Route.useLoaderData();

  const { data: queryCoach } = useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  if (!queryCoach || !resource) {
    return <ResourceSkeleton />;
  }

  const externalResourceUrl = resource.externalLinkUrl?.trim() || "";
  const externalResourceLabel =
    resource.externalLinkText?.trim() || "Download this resource";

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-12">
      <Card>
        <CardContent>
          {/* Top Title Section */}
          <div className="mb-8 flex flex-col">
            {resource.publishedAt && (
              <p className="mb-4 text-sm font-bold text-brand-700">
                Published on{" "}
                {new Intl.DateTimeFormat(undefined).format(
                  resource.publishedAt,
                )}
              </p>
            )}
            <h1 className="text-text-primary mb-2 font-heading text-4xl">
              {resource.title}
            </h1>
            <p className="text-text-secondary mb-4 text-lg">
              {resource.description}
            </p>
            {resource.imageSrc && (
              <CDNImage
                src={resource.imageSrc}
                priority
                alt={resource.title}
                width={1200}
                height={630}
                className="aspect-40/21 w-full rounded-lg object-cover"
              />
            )}
          </div>

          {/* Main Content Section */}
          <div className="prose prose-lg max-w-none leading-relaxed text-gray-700">
            <main>
              <MarkdownContent ugc content={resource.content} />
            </main>

            {externalResourceUrl && (
              <div className="mt-8 mb-4 flex items-center justify-center">
                <ButtonLink
                  ugc
                  href={externalResourceUrl}
                  target="_blank"
                  className="flex items-center gap-2"
                >
                  <span>{externalResourceLabel}</span>
                  <ExternalLink />
                </ButtonLink>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {/* Footer or Related Articles */}
      {relatedResources.length > 0 && (
        <div className="mt-16">
          <h2 className="mb-4 font-heading text-2xl">
            Latest Coaching Insights
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {relatedResources.map((relatedResource) => (
              <ResourceCard
                key={relatedResource.id}
                resource={relatedResource}
                coachUsername={queryCoach.username}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
